// app/javascript/controllers/multiselect_autocomplete_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
	static targets = ["input", "suggestions", "selectedItems", "container"]
	static values = {
		items: { type: Array, default: [] },
		url: { type: String, default: '' },
		queryParam: { type: String, default: 'q' },
		debounce: { type: Number, default: 300 },
		remote: { type: String, default: 'static' }, // static, once, each
		labelKey: { type: String, default: 'label' },
		valueKey: { type: String, default: 'value' },
		name: { type: String, default: '' },
		selected: { type: Array, default: [] },
		maxTagWidth: { type: Number, default: 180 } // Default max width for tags in pixels
	}

	connect() {
		this.selectedItems = []
		this.filteredSuggestions = []
		this.currentFocusIndex = -1
		this.debounceTimer = null

		if (this.selectedValue && this.selectedValue.length > 0) {
			this.selectedItems = this.selectedValue
			this.renderSelectedItems()
		}

		if (this.remoteValue === 'once') {
			this.fetchItems()
		}
	}

	inputFocus() {
		if (this.inputTarget.value.length > 0) {
			this.showSuggestions()
		}
	}

	inputBlur() {
		// Delay hiding suggestions to allow clicks to register
		setTimeout(() => {
			this.hideSuggestions()
		}, 150)
	}

	containerClick(event) {
		if (event.target === this.containerTarget ||
			(this.containerTarget.contains(event.target) &&
				!this.suggestionsTarget.contains(event.target))) {
			this.inputTarget.focus()
		}
	}

	inputInput() {
		const value = this.inputTarget.value.trim()
		console.log("Input value:", value)
		console.log("Remote value:", this.remoteValue)

		if (value === "") {
			this.hideSuggestions()
			return
		}

		if (this.remoteValue === 'each') {
			// Debounce API requests
			clearTimeout(this.debounceTimer)
			this.debounceTimer = setTimeout(() => {
				this.fetchFilteredItems(value)
			}, this.debounceValue)
		} else {
			this.filterSuggestions(value)
		}
	}

	keyDown(event) {
		const inputValue = this.inputTarget.value.trim()
		const isSuggestionsVisible = !this.suggestionsTarget.classList.contains('hidden')

		switch (event.key) {
			case "Escape":
				if (isSuggestionsVisible) {
					event.preventDefault()
					this.hideSuggestions()
				}
				break

			case "ArrowUp":
				if (isSuggestionsVisible) {
					event.preventDefault()
					this.navigateSuggestion(-1)
				}
				break

			case "ArrowDown":
			case "Tab":
				if (isSuggestionsVisible) {
					event.preventDefault()
					this.navigateSuggestion(1)
				} else if (inputValue) {
					event.preventDefault()
					this.showSuggestions()
					this.navigateSuggestion(1)
				}
				break

			case "Enter":
				if (isSuggestionsVisible && this.currentFocusIndex >= 0) {
					event.preventDefault()
					this.addItem(this.filteredSuggestions[this.currentFocusIndex])
				}
				break

			case "Backspace":
				if (inputValue === "" && this.selectedItems.length > 0) {
					this.removeLastItem()
				}
				break
		}
	}

	selectItem(event) {
		const index = event.currentTarget.dataset.index
		this.addItem(this.filteredSuggestions[index])
	}

	removeItem(event) {
		const index = event.currentTarget.dataset.index
		this.selectedItems.splice(index, 1)
		this.renderSelectedItems()
	}

	filterSuggestions(query) {
		this.filteredSuggestions = this.itemsValue.filter(item => {
			const label = item[this.labelKeyValue].toLowerCase()
			const searchTerm = query.toLowerCase()

			return label.includes(searchTerm) &&
				!this.selectedItems.some(selected => selected[this.valueKeyValue] === item[this.valueKeyValue])
		})

		if (this.filteredSuggestions.length > 0) {
			this.renderSuggestions()
			this.showSuggestions()
		} else {
			this.hideSuggestions()
		}
	}

	async fetchItems() {
		const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

		try {
			const response = await fetch(this.urlValue, {credentials: "same-origin", headers: {'X-CSRF-Token': csrfToken}})
			if (!response.ok) throw new Error('Network response was not ok')

			const data = await response.json()
			this.itemsValue = data
		} catch (error) {
			console.error('Error fetching items:', error)
		}
	}

	async fetchFilteredItems(query) {
		const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

		try {
			const url = new URL(this.urlValue)
			url.searchParams.set(this.queryParamValue, query)

			const response = await fetch(url, {credentials: "same-origin", headers: {'X-CSRF-Token': csrfToken}})
			if (!response.ok) throw new Error('Network response was not ok')

			const data = await response.json()
			this.filteredSuggestions = data.filter(item =>
				!this.selectedItems.some(selected => selected[this.valueKeyValue] === item[this.valueKeyValue])
			)

			if (this.filteredSuggestions.length > 0) {
				this.renderSuggestions()
				this.showSuggestions()
			} else {
				this.hideSuggestions()
			}
		} catch (error) {
			console.error('Error fetching filtered items:', error)
		}
	}

	navigateSuggestion(direction) {
		const newIndex = this.currentFocusIndex + direction

		if (newIndex >= 0 && newIndex < this.filteredSuggestions.length) {
			this.currentFocusIndex = newIndex
		} else if (direction < 0 && newIndex < 0) {
			// Loop to bottom if going up from top
			this.currentFocusIndex = this.filteredSuggestions.length - 1
		} else if (direction > 0 && newIndex >= this.filteredSuggestions.length) {
			// Loop to top if going down from bottom
			this.currentFocusIndex = 0
		}

		this.updateSuggestionFocus()
	}

	updateSuggestionFocus() {
		// Remove focus from all items
		const items = this.suggestionsTarget.querySelectorAll('li')
		items.forEach(item => item.classList.remove('bg-emerald-100'))

		// Add focus to current item
		if (this.currentFocusIndex >= 0 && items[this.currentFocusIndex]) {
			const focusedItem = items[this.currentFocusIndex]
			focusedItem.classList.add('bg-emerald-100')

			// Auto-scroll to keep focused item visible
			focusedItem.scrollIntoView({ block: 'nearest' })

			// Update ARIA
			this.inputTarget.setAttribute('aria-activedescendant', `suggestion-${this.currentFocusIndex}`)
		} else {
			this.inputTarget.setAttribute('aria-activedescendant', '')
		}
	}

	addItem(item) {
		if (!this.selectedItems.some(selected => selected[this.valueKeyValue] === item[this.valueKeyValue])) {
			this.selectedItems.push(item)
			this.renderSelectedItems()
			this.inputTarget.value = ''
			this.hideSuggestions()
			this.inputTarget.focus()
		}
	}

	removeLastItem() {
		if (this.selectedItems.length > 0) {
			this.selectedItems.pop()
			this.renderSelectedItems()
		}
	}

	renderSuggestions() {
		this.suggestionsTarget.innerHTML = ''

		this.filteredSuggestions.forEach((item, index) => {
			const li = document.createElement('li')
			li.id = `suggestion-${index}`
			li.textContent = item[this.labelKeyValue]
			li.className = 'px-3 py-2 cursor-pointer hover:bg-emerald-50 text-sm'
			li.setAttribute('role', 'option')
			li.setAttribute('data-index', index)
			li.setAttribute('data-action', 'click->multiselect-autocomplete#selectItem')

			if (index === this.currentFocusIndex) {
				li.classList.add('bg-emerald-100')
			}

			this.suggestionsTarget.appendChild(li)
		})
	}

	renderSelectedItems() {
		// Clear existing items
		this.selectedItemsTarget.innerHTML = ''

		// Add selected items with truncation
		this.selectedItems.forEach((item, index) => {
			const hiddenInput = document.createElement('input')
			hiddenInput.type = 'hidden'
			hiddenInput.name = this.nameValue
			hiddenInput.value = item[this.valueKeyValue]
			this.selectedItemsTarget.appendChild(hiddenInput)

			const itemElement = document.createElement('div')
			itemElement.className = 'flex items-center bg-emerald-100 text-emerald-800 rounded px-2 py-1 text-sm mr-1 mb-1'

			// Create text span with truncation
			const textSpan = document.createElement('span')
			textSpan.className = 'truncate' // Add truncate class
			textSpan.style.maxWidth = `${this.maxTagWidthValue}px` // Set max width for truncation
			textSpan.title = item[this.labelKeyValue] // Show full text on hover
			textSpan.textContent = item[this.labelKeyValue]
			itemElement.appendChild(textSpan)

			// Create remove button
			const removeButton = document.createElement('button')
			removeButton.type = 'button'
			removeButton.className = 'ml-1 text-emerald-600 hover:text-emerald-800 focus:outline-none flex-shrink-0' // Add flex-shrink-0 to prevent button from shrinking
			removeButton.innerHTML = '&times;'
			removeButton.setAttribute('aria-label', `Remove ${item[this.labelKeyValue]}`)
			removeButton.setAttribute('data-index', index)
			removeButton.setAttribute('data-action', 'click->multiselect-autocomplete#removeItem')

			itemElement.appendChild(removeButton)
			this.selectedItemsTarget.appendChild(itemElement)
		})
	}

	showSuggestions() {
		this.suggestionsTarget.classList.remove('hidden')
		this.inputTarget.setAttribute('aria-expanded', 'true')
	}

	hideSuggestions() {
		this.suggestionsTarget.classList.add('hidden')
		this.currentFocusIndex = -1
		this.inputTarget.setAttribute('aria-expanded', 'false')
		this.inputTarget.setAttribute('aria-activedescendant', '')
	}
}
